import {useMemo} from "react";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {CROPS, isCortevaCornSeedCrop} from "../utils/enumItemsUtils";
import {Crop} from "../types/domain/crop";
import {useQuery} from "@tanstack/react-query";
import {getCropIds} from "../services/fieldService";
import {queryClient} from "../utils/queryClient";
import {useCompanyFeatureFlag} from "./featureFlagsHook";
import {FeatureFlagEnum} from "../types/featureFlagsEnum";

export const useCrops = () => {
    const {t} = useTranslation();
    const showCortevaCornSeedCrops = useCompanyFeatureFlag(FeatureFlagEnum.cortevaCornSeedCrops);

    return useMemo(() => {
        const allCrops: Crop[] = CROPS.map((item) => ({id: item.id, name: t(`enumerations.crops.${item.i18nKey}`)}));
        const crops = showCortevaCornSeedCrops ? allCrops : allCrops.filter((crop) => !isCortevaCornSeedCrop(crop.id));

        return {
            crops,
            cropsById: _.keyBy(allCrops, "id"),
        };
    }, [showCortevaCornSeedCrops, t]);
};

export const useCompanyCrops = (companyId?: string) => {
    const {t} = useTranslation();
    const showCortevaCornSeedCrops = useCompanyFeatureFlag(FeatureFlagEnum.cortevaCornSeedCrops);

    return useQuery({
        queryKey: ["companyCrops", companyId, showCortevaCornSeedCrops],
        queryFn: async () => {
            if (!companyId) return [];

            const crops = await getCropIds(companyId);
            return crops
                .filter((cropId) => showCortevaCornSeedCrops || !isCortevaCornSeedCrop(cropId))
                .map((cropId) => {
                    const crop = CROPS.find((CROP) => CROP.id === cropId);
                    return {id: cropId, name: t("enumerations.crops." + crop?.i18nKey) ?? ""};
                });
        },
        enabled: !!companyId,
        staleTime: Infinity,
        gcTime: Infinity,
    });
};

export const invalidateCropsQueries = (companyId: string) =>
    void queryClient.invalidateQueries({queryKey: ["companyCrops", companyId]});
